import { useAuth } from '@/hooks/useAuth';
import { useConfig, useCotacoes } from '@/hooks/useDB';
import { accessLevel, canManageMasterData, canAccessQuote } from '@/auth/access';
import db, { type Cotacao, type User } from '@/db';
import { Link } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { listRequests } from '@/features/requests/store';
import { canView, closed, type RequestRecord } from '@/features/requests/model';
import {supabaseConfigured} from '@/services/supabaseClient';
import {listCloudUsers} from '@/services/cloudUsers';

type AmostraResumo = {
  vendedor: string;
  status: 'Solicitada' | 'Em preparação' | 'Enviada' | 'Concluída';
};

const normalizeName = (value?: string) => (value || '')
  .normalize('NFD')
  .replace(/[\u0300-\u036f]/g, '')
  .trim()
  .toLowerCase();

const money = (value: number) => new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  maximumFractionDigits: 0,
}).format(value);

const quoteValueBrl = (quote: Cotacao) => {
  const value = Number(quote.totalBrl ?? quote.total ?? 0);
  return Number.isFinite(value) ? value : 0;
};

const marginPercent = (quote: Cotacao) => {
  const value = Number(quote.margemMedia);
  if (!Number.isFinite(value)) return null;
  return Math.abs(value) <= 1 ? value * 100 : value;
};

const modules = [
  { title: 'Projetos e Amostras', description: 'Solicitações comerciais reunidas em um único fluxo.', path: '/solicitacoes', icon: 'ri-flask-line', color: 'from-blue-600 to-primary-700', adminOnly: true },
  {
    title: 'Calculadora de Preços',
    description: 'Calcule preços com regras fiscais automáticas e margem de lucro.',
    path: '/calculadora',
    icon: 'ri-calculator-line',
    color: 'from-blue-600 to-primary-700',
  },
  {
    title: 'Tabela de Preços',
    description: 'Consulte a tabela de preços dos seus clientes e produtos.',
    path: '/tabela-precos',
    icon: 'ri-price-tag-3-line',
    color: 'from-slate-500 to-slate-700',
  },
  {
    title: 'Tabelas de Cadastro',
    description: 'Gerencie clientes e materiais do sistema.',
    path: '/tabelas',
    icon: 'ri-table-line',
    color: 'from-indigo-500 to-indigo-700',
    adminOnly: true,
  },
  {
    title: 'Cotações',
    description: 'Histórico completo de cotações geradas pela equipe.',
    path: '/cotacoes',
    icon: 'ri-file-list-3-line',
    color: 'from-sky-500 to-blue-600',
  },
  {
    title: 'Indicadores',
    description: 'KPIs e gráficos de performance comercial.',
    path: '/indicadores',
    icon: 'ri-bar-chart-box-line',
    color: 'from-violet-500 to-indigo-700',
  },
];

export default function HomePage() {
  const { user } = useAuth();
  const { config } = useConfig();
  const { cotacoes, loading: loadingCotacoes } = useCotacoes();
  const [now, setNow] = useState(() => new Date());
  const [teamSellers, setTeamSellers] = useState<User[]>([]);
  const [loadingTeam, setLoadingTeam] = useState(false);
  const [amostras, setAmostras] = useState<AmostraResumo[]>([]);
  const [requests, setRequests] = useState<RequestRecord[]>([]);
  const isAdmin = accessLevel(user) === 'admin';
  useEffect(() => {
    if (!isAdmin) { setRequests([]); return; }
    listRequests().then(setRequests).catch(() => setRequests([]));
  }, [isAdmin]);
  const newPendingSamples = isAdmin ? requests.filter(r => r.kind === 'amostra' && canView(r, user) && r.status !== 'Rascunho' && !closed.has(r.status)).length : 0;
  useEffect(() => {
    const timer = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(timer);
  }, []);
  useEffect(() => {
    if (accessLevel(user) !== 'manager' || !user?.email) { setTeamSellers([]); setLoadingTeam(false); return; }
    setLoadingTeam(true);
    (supabaseConfigured ? listCloudUsers().then(rows=>rows.filter(member=>member.managerEmail===user.email&&accessLevel(member)==='seller')) : db.users.where('managerEmail').equals(user.email).filter((member) => accessLevel(member) === 'seller').toArray())
      .then(setTeamSellers)
      .catch(() => setTeamSellers([]))
      .finally(() => setLoadingTeam(false));
  }, [user]);
  useEffect(() => {
    if (!isAdmin) { setAmostras([]); return; }
    try { setAmostras(JSON.parse(localStorage.getItem('zsb-amostras-v1') || '[]')); } catch { setAmostras([]); }
  }, [isAdmin]);

  const teamNames = useMemo(() => new Set(teamSellers.map((seller) => normalizeName(seller.name))), [teamSellers]);
  const visibleQuotes = useMemo(() => cotacoes.filter(quote => canAccessQuote(user, quote.vendedor)), [cotacoes, user]);
  const visibleSamples = useMemo(() => amostras.filter(sample => canAccessQuote(user, sample.vendedor)), [amostras, user]);

  const dashboard = useMemo(() => {
    const saoPauloDate = (date: Date) => new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/Sao_Paulo', year: 'numeric', month: '2-digit', day: '2-digit',
    }).format(date);
    const today = saoPauloDate(now);
    const month = today.slice(0, 7);
    const quoteDate = (quote: Cotacao) => {
      const date = new Date(quote.createdAt);
      return Number.isNaN(date.getTime()) ? '' : saoPauloDate(date);
    };
    const quotesToday = visibleQuotes.filter((quote) => quoteDate(quote) === today).length;
    const monthQuotes = visibleQuotes.filter((quote) => quoteDate(quote).startsWith(month));
    return {
      quotesToday,
      monthTotal: monthQuotes.reduce((sum, quote) => sum + quoteValueBrl(quote), 0),
      lowMargin: visibleQuotes.filter((quote) => {
        const margin = marginPercent(quote);
        return margin !== null && margin < 40;
      }).length,
      pendingSamples: visibleSamples.filter((sample) => sample.status !== 'Concluída').length + newPendingSamples,
      recent: [...visibleQuotes].sort((a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt)).slice(0, 4),
    };
  }, [now, visibleQuotes, visibleSamples, newPendingSamples]);
  const visibleModules = modules.filter((mod) => !mod.adminOnly || canManageMasterData(user));
  const loadingDashboard = loadingCotacoes || loadingTeam;
  const formatRate = (value?: number) => value?.toLocaleString('pt-BR', { minimumFractionDigits: 4, maximumFractionDigits: 4 }) || '—';
  const updatedAt = (() => {
    if (!config?.cambioAtualizadoEm) return 'Última atualização: aguardando atualização';
    const date = new Date(config.cambioAtualizadoEm);
    const fullDate = date.toLocaleDateString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
    const time = date.toLocaleTimeString('pt-BR', {
      timeZone: 'America/Sao_Paulo',
      hour: '2-digit',
      minute: '2-digit',
    });
    return `Última atualização: ${fullDate} às ${time}`;
  })();

  const brasiliaParts = new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo', weekday: 'long', day: '2-digit', month: 'long', year: 'numeric', hour: '2-digit', hourCycle: 'h23',
  }).formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) => brasiliaParts.find((item) => item.type === type)?.value || '';
  const hour = Number(part('hour'));
  const dateMessage = `Hoje é ${part('weekday')}, dia ${part('day')} de ${part('month')} de ${part('year')}.`;
  const workMessage = hour < 6
    ? 'Hey, já é madrugada. Que tal um chá bem quente?'
    : hour < 12
      ? 'Tenha um excelente dia de trabalho.'
      : hour < 18
        ? 'Tenha uma excelente tarde.'
        : 'Tenha uma ótima noite.';

  return (
    <div className="mx-auto max-w-[1500px] p-4 md:p-6 lg:p-8">
      <section className="home-reveal relative overflow-hidden rounded-[28px] bg-primary-950 text-white shadow-xl shadow-primary-950/15">
        <img src="/brand/login-background.png" alt="" className="absolute inset-0 h-full w-full object-cover object-center" />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/95 via-primary-900/80 to-primary-800/30" />
        <div className="relative grid min-h-[310px] items-center gap-8 px-6 py-8 md:px-10 lg:grid-cols-[1fr_0.9fr] lg:px-12">
          <div className="max-w-2xl">
            <div className="mb-5 flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/20 bg-white/10 backdrop-blur"><i className={hour < 6 ? 'ri-cup-line' : hour < 12 ? 'ri-sun-line' : hour < 18 ? 'ri-sun-foggy-line' : 'ri-moon-clear-line'} /></span><span className="text-xs font-bold uppercase tracking-[0.2em] text-cyan-100/75">Seu painel de trabalho</span></div>
            <h1 className="text-3xl font-extrabold tracking-tight md:text-4xl">Olá, {user?.name?.split(' ')[0] || 'usuário'}.</h1>
            <p className="mt-3 text-base text-white/80 md:text-lg">{dateMessage}</p>
            <p className="mt-1 text-base font-semibold text-white">{workMessage}</p>
          </div>
          <div className="grid grid-cols-2 gap-2 rounded-2xl border border-white/15 bg-primary-950/30 p-3 backdrop-blur-md sm:grid-cols-3 lg:grid-cols-2">
            {visibleModules.map((mod) => <Link key={mod.path} to={mod.path} className="group flex min-h-16 items-center gap-3 rounded-xl border border-white/10 bg-white/10 px-3 py-3 transition hover:-translate-y-0.5 hover:border-white/25 hover:bg-white/20"><span className={`flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${mod.color} text-white shadow`}><i className={mod.icon} /></span><span className="min-w-0"><span className="block text-sm font-bold leading-tight text-white">{mod.title.replace(' de Preços', '')}</span><span className="mt-1 block text-[10px] text-white/55 transition group-hover:text-white/75">Abrir módulo <i className="ri-arrow-right-line" /></span></span></Link>)}
          </div>
        </div>
      </section>

      <section className="home-reveal home-reveal-delay-1 relative mt-5">
        <div className="grid overflow-hidden rounded-2xl border border-background-200 bg-white shadow-lg shadow-primary-950/5 md:grid-cols-[170px_1fr_1fr_1.35fr]">
          <div className="flex items-center gap-3 bg-primary-700 px-5 py-4 text-white"><span className="flex h-9 w-9 items-center justify-center rounded-lg bg-white/10"><i className="ri-exchange-dollar-line" /></span><div><p className="text-xs font-bold uppercase tracking-wider">Câmbio PTAX</p><p className="text-[10px] text-white/60">Automático</p></div></div>
          <div className="border-b border-background-100 px-5 py-4 md:border-b-0 md:border-r"><p className="text-[10px] font-bold uppercase tracking-wider text-foreground-500">Dólar · USD</p><p className="mt-1 font-mono text-lg font-bold text-foreground-950">R$ {formatRate(config?.cotacaoUsd)}</p></div>
          <div className="border-b border-background-100 px-5 py-4 md:border-b-0 md:border-r"><p className="text-[10px] font-bold uppercase tracking-wider text-foreground-500">Euro · EUR</p><p className="mt-1 font-mono text-lg font-bold text-foreground-950">R$ {formatRate(config?.cotacaoEur)}</p></div>
          <div className="flex items-center px-5 py-4"><div><span className="inline-flex items-center gap-2 text-xs font-bold text-emerald-700"><span className="h-2 w-2 rounded-full bg-emerald-500 ring-4 ring-emerald-100" />Atualização diária</span><p className="mt-1 text-[11px] leading-relaxed text-foreground-500">{updatedAt}</p></div></div>
        </div>
      </section>

      <section className="home-reveal home-reveal-delay-2 mt-7 pb-4">
        <div className="mb-4 flex items-end justify-between gap-4">
          <div>
            <p className="text-[10px] font-extrabold uppercase tracking-[0.2em] text-primary-600">Visão comercial</p>
            <h2 className="mt-1 text-xl font-extrabold tracking-tight text-foreground-950">{accessLevel(user) === 'seller' ? 'Suas cotações recentes' : 'Resumo do dia'}</h2>
          </div>
          <Link to="/cotacoes" className="text-xs font-bold text-primary-600 transition hover:text-primary-800">Ver todas as cotações <i className="ri-arrow-right-line" /></Link>
        </div>

        {accessLevel(user) !== 'seller' && <div className="home-stagger grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            { label: 'Cotações criadas hoje', value: loadingDashboard ? '—' : dashboard.quotesToday.toString(), icon: 'ri-file-add-line', tone: 'bg-blue-50 text-blue-700' },
            { label: 'Valor cotado no mês', value: loadingDashboard ? '—' : money(dashboard.monthTotal), icon: 'ri-funds-line', tone: 'bg-emerald-50 text-emerald-700' },
            { label: 'Propostas abaixo de 40%', value: loadingDashboard ? '—' : dashboard.lowMargin.toString(), icon: 'ri-error-warning-line', tone: 'bg-amber-50 text-amber-700' },
            ...(isAdmin ? [{ label: 'Amostras em andamento', value: loadingTeam ? '—' : dashboard.pendingSamples.toString(), icon: 'ri-flask-line', tone: 'bg-violet-50 text-violet-700' }] : []),
          ].map((card) => <div key={card.label} className="flex items-center gap-4 rounded-2xl border border-background-200 bg-white p-4 shadow-sm transition hover:-translate-y-0.5 hover:shadow-md"><span className={`flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl text-lg ${card.tone}`}><i className={card.icon} /></span><div className="min-w-0"><p className="truncate text-[10px] font-bold uppercase tracking-wider text-foreground-500">{card.label}</p><p className="mt-1 truncate text-xl font-extrabold text-foreground-950">{card.value}</p></div></div>)}
        </div>}

        <div className={`${accessLevel(user) === 'seller' ? '' : 'mt-4'} overflow-hidden rounded-2xl border border-background-200 bg-white shadow-sm`}>
          <div className="flex items-center justify-between border-b border-background-100 px-5 py-4">
            <div><h3 className="text-sm font-extrabold text-foreground-950">Cotações recentes</h3><p className="mt-0.5 text-[11px] text-foreground-500">{accessLevel(user) === 'seller' ? 'Somente as suas últimas propostas' : 'Últimas propostas disponíveis para o seu perfil'}</p></div>
            <span className="rounded-full bg-primary-50 px-2.5 py-1 text-[10px] font-bold text-primary-700">{loadingDashboard ? 'Carregando…' : `${visibleQuotes.length} no histórico`}</span>
          </div>
          {dashboard.recent.length === 0 ? <div className="px-5 py-8 text-center text-sm text-foreground-500">Nenhuma cotação disponível até o momento.</div> : <div className="divide-y divide-background-100">{dashboard.recent.map((quote: Cotacao) => <Link key={quote.id} to="/cotacoes" className="grid items-center gap-2 px-5 py-3 transition hover:bg-background-50 sm:grid-cols-[minmax(0,1.4fr)_minmax(120px,0.7fr)_110px_130px_20px]"><div className="min-w-0"><p className="truncate text-sm font-bold text-foreground-900">{quote.clienteNome}</p><p className="truncate text-[11px] text-foreground-500">{quote.itens.map((item) => item.materialDescricao).join(', ')}</p></div><p className="truncate text-xs font-semibold text-foreground-700">{quote.vendedor}</p><p className="text-xs text-foreground-500">{new Date(quote.createdAt).toLocaleDateString('pt-BR', { timeZone: 'America/Sao_Paulo' })}</p><p className="text-right font-mono text-xs font-bold text-foreground-900">{money(quoteValueBrl(quote))}</p><i className="ri-arrow-right-s-line text-foreground-400" /></Link>)}</div>}
        </div>
      </section>

    </div>
  );
}
